/**
 * RagWeb Search Widget — <ragweb-search>
 * Embeddable AI search widget with closed Shadow DOM.
 * Zero external dependencies. CSP compatible.
 *
 * Usage:
 * <script src="https://ragweb.fr/widget/v1.js"
 *   data-api-key="rw_xxx"
 *   data-api-url="https://api.ragweb.fr/search"
 *   data-color="#2563eb"
 *   data-position="bottom-right"
 *   data-shape="circle"
 *   data-icon="search"
 *   data-placeholder="Posez votre question..."
 *   data-title="Recherche IA"
 *   data-lang="fr"
 *   data-welcome="Bienvenue ! Posez votre question."
 *   data-branding="true">
 * </script>
 *
 * data-api-url: endpoint API (défaut: https://api.ragweb.fr/search)
 * data-shape options: circle (default), rounded, square, pill
 * data-icon options: search (default), chat, sparkle, help, book
 */
(function () {
  'use strict';

  var SCRIPT = document.currentScript || document.querySelector('script[data-ragweb-widget]');
  const DEFAULT_API_URL = 'https://api.ragweb.fr/search';

  const I18N = {
    fr: {
      placeholder: 'Posez votre question...',
      title: 'Recherche IA',
      error_network: 'Erreur réseau. Veuillez réessayer.',
      error_rate_limit: 'Trop de requêtes. Veuillez patienter.',
      error_invalid: 'Question invalide.',
      error_expired: 'Essai terminé.',
      error_server: 'Service temporairement indisponible.',
      powered_by: 'Propulsé par',
      ai_disclosure: 'Réponses générées par IA',
      sources: 'Sources',
    },
    en: {
      placeholder: 'Ask a question...',
      title: 'AI Search',
      error_network: 'Network error. Please try again.',
      error_rate_limit: 'Too many requests. Please wait.',
      error_invalid: 'Invalid query.',
      error_expired: 'Trial expired.',
      error_server: 'Service temporarily unavailable.',
      powered_by: 'Powered by',
      ai_disclosure: 'AI-generated responses',
      sources: 'Sources',
    },
  };

  // Read configuration from script data attributes
  function getConfig() {
    var scriptEl = document.querySelector('script[data-ragweb-widget]') || SCRIPT;
    const lang = (scriptEl.getAttribute('data-lang') || 'fr').toLowerCase();
    const validLang = lang === 'en' ? 'en' : 'fr';
    const shape = (scriptEl.getAttribute('data-shape') || 'circle').toLowerCase();
    const validShape = ['circle', 'rounded', 'square', 'pill'].includes(shape) ? shape : 'circle';
    const icon = (scriptEl.getAttribute('data-icon') || 'search').toLowerCase();
    const validIcon = ['search', 'chat', 'sparkle', 'help', 'book'].includes(icon) ? icon : 'search';
    return {
      apiKey: scriptEl.getAttribute('data-api-key') || '',
      apiUrl: scriptEl.getAttribute('data-api-url') || DEFAULT_API_URL,
      color: scriptEl.getAttribute('data-color') || '#2563eb',
      position: scriptEl.getAttribute('data-position') || 'bottom-right',
      shape: validShape,
      icon: validIcon,
      placeholder: scriptEl.getAttribute('data-placeholder') || I18N[validLang].placeholder,
      title: scriptEl.getAttribute('data-title') || I18N[validLang].title,
      lang: validLang,
      welcome: scriptEl.getAttribute('data-welcome') || '',
      branding: scriptEl.getAttribute('data-branding'),
      includePaths: scriptEl.getAttribute('data-include-paths') || '',
      excludePaths: scriptEl.getAttribute('data-exclude-paths') || '',
      escalationUrl: scriptEl.getAttribute('data-escalation-url') || '',
      escalationLabel: scriptEl.getAttribute('data-escalation-label') || (validLang === 'en' ? 'Talk to a human' : 'Parler à un humain'),
      streamUrl: scriptEl.getAttribute('data-stream-url') || '',
    };
  }

  function shouldShowOnPage(config) {
    var path = window.location.pathname;

    // If include-paths is set, only show on matching paths
    if (config.includePaths) {
      var includes = config.includePaths.split(',').map(function(p) { return p.trim(); });
      var matched = includes.some(function(pattern) { return matchPath(path, pattern); });
      if (!matched) return false;
    }

    // If exclude-paths is set, hide on matching paths
    if (config.excludePaths) {
      var excludes = config.excludePaths.split(',').map(function(p) { return p.trim(); });
      var excluded = excludes.some(function(pattern) { return matchPath(path, pattern); });
      if (excluded) return false;
    }

    return true;
  }

  function matchPath(path, pattern) {
    // Simple glob matching: * matches any segment, ** matches anything
    if (pattern === '*') return true;
    if (pattern.endsWith('/*')) {
      return path.startsWith(pattern.slice(0, -1));
    }
    if (pattern.endsWith('/**')) {
      return path.startsWith(pattern.slice(0, -2));
    }
    return path === pattern || path === pattern + '/';
  }

  // YouTube SVG icon (inline, 14×14, red)
  const YT_ICON_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="#ff0000" xmlns="http://www.w3.org/2000/svg" style="display:inline-block;vertical-align:middle;margin-right:3px;flex-shrink:0;"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>';

  // Detect YouTube URLs
  function isYouTubeUrl(url) {
    return /(?:youtube\.com\/watch|youtu\.be\/)/i.test(url);
  }

  /**
   * Minimal markdown to HTML renderer.
   * Supports: **bold**, *italic*, [links](url), `code`, numbered lists, bullet lists, line breaks.
   * YouTube links get a red icon prepended.
   */
  function renderMarkdown(text) {
    if (!text) return '';
    var html = text
      // Escape HTML
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Headings: ####, ###, ##, # (must be before bold/italic)
      .replace(/^####\s+(.+)$/gm, '<strong style="display:block;font-size:13px;margin-top:10px;margin-bottom:2px;">$1</strong>')
      .replace(/^###\s+(.+)$/gm, '<strong style="display:block;font-size:14px;margin-top:12px;margin-bottom:3px;">$1</strong>')
      .replace(/^##\s+(.+)$/gm, '<strong style="display:block;font-size:15px;margin-top:14px;margin-bottom:4px;">$1</strong>')
      .replace(/^#\s+(.+)$/gm, '<strong style="display:block;font-size:16px;margin-top:14px;margin-bottom:4px;">$1</strong>')
      // Bold: **text** or __text__
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.+?)__/g, '<strong>$1</strong>')
      // Italic: *text* or _text_
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/_(.+?)_/g, '<em>$1</em>')
      // Inline code: `code`
      .replace(/`(.+?)`/g, '<code style="background:#e5e7eb;padding:1px 4px;border-radius:3px;font-size:12px;">$1</code>')
      // Links: [text](url) — YouTube links get icon + red style
      .replace(/\[(.+?)\]\((.+?)\)/g, function(_match, linkText, url) {
        if (isYouTubeUrl(url)) {
          return '<a href="' + url + '" target="_blank" rel="noopener" class="rw-yt-link">' + YT_ICON_SVG + linkText + '</a>';
        }
        return '<a href="' + url + '" target="_blank" rel="noopener" style="color:inherit;text-decoration:underline;">' + linkText + '</a>';
      })
      // Numbered lists: lines starting with "1. ", "2. ", etc.
      .replace(/^(\d+)\.\s+(.+)$/gm, '<li style="margin-left:1.25em;list-style:decimal;">$2</li>')
      // Bullet lists: lines starting with "- " or "* "
      .replace(/^[-*]\s+(.+)$/gm, '<li style="margin-left:1.25em;list-style:disc;">$1</li>')
      // Line breaks (double newline = paragraph, single = br)
      .replace(/\n\n/g, '</p><p style="margin-top:8px;">')
      .replace(/\n/g, '<br>');

    return '<p>' + html + '</p>';
  }

  // Generate widget styles
  function getStyles(config) {
    var pos, panelPos;
    switch (config.position) {
      case 'bottom-left':
        pos = 'left: 20px; bottom: 20px;';
        panelPos = 'left: 20px; bottom: 88px;';
        break;
      case 'top-right':
        pos = 'right: 20px; top: 20px;';
        panelPos = 'right: 20px; top: 88px;';
        break;
      case 'middle-right':
        pos = 'right: 20px; top: 50%; transform: translateY(-50%);';
        panelPos = 'right: 20px; top: 50%; transform: translateY(-50%);';
        break;
      default: // bottom-right
        pos = 'right: 20px; bottom: 20px;';
        panelPos = 'right: 20px; bottom: 88px;';
        break;
    }

    return `
      :host {
        all: initial;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
        font-size: 14px;
        line-height: 1.5;
        color: #1f2937;
      }

      * {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
      }

      .rw-btn {
        position: fixed;
        ${pos}
        ${config.shape === 'pill' ? 'min-width: 56px; height: 48px; padding: 0 18px; gap: 8px;' : 'width: 56px; height: 56px;'}
        border-radius: ${config.shape === 'circle' ? '50%' : config.shape === 'rounded' ? '14px' : config.shape === 'pill' ? '24px' : '8px'};
        background: ${config.color};
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        z-index: 2147483646;
      }

      .rw-btn:hover {
        transform: ${config.position === 'middle-right' ? 'translateY(-50%) scale(1.05)' : 'scale(1.05)'};
        box-shadow: 0 6px 16px rgba(0,0,0,0.2);
      }

      .rw-btn:focus-visible {
        outline: 2px solid ${config.color};
        outline-offset: 3px;
      }

      .rw-btn svg {
        width: 24px;
        height: 24px;
        fill: #fff;
        flex-shrink: 0;
      }

      .rw-btn-label {
        color: #fff;
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
      }

      .rw-panel {
        position: fixed;
        ${panelPos}
        width: 380px;
        max-width: calc(100vw - 40px);
        height: 520px;
        max-height: calc(100vh - 120px);
        background: #fff;
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08);
        display: none;
        flex-direction: column;
        overflow: hidden;
        z-index: 2147483647;
        animation: rw-slide-up 0.2s ease-out;
      }

      .rw-panel.rw-open {
        display: flex;
      }

      @keyframes rw-slide-up {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .rw-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 16px;
        background: ${config.color};
        color: #fff;
        flex-shrink: 0;
      }

      .rw-header-title {
        font-size: 15px;
        font-weight: 600;
      }

      .rw-close {
        background: none;
        border: none;
        cursor: pointer;
        color: #fff;
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: background 0.15s;
      }

      .rw-close:hover {
        background: rgba(255,255,255,0.2);
      }

      .rw-close:focus-visible {
        outline: 2px solid #fff;
        outline-offset: 2px;
      }

      .rw-close svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
      }

      .rw-messages {
        flex: 1;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .rw-msg {
        max-width: 90%;
        padding: 10px 14px;
        border-radius: 10px;
        font-size: 13px;
        line-height: 1.5;
        word-wrap: break-word;
      }

      .rw-msg-user {
        align-self: flex-end;
        background: ${config.color};
        color: #fff;
        border-bottom-right-radius: 4px;
      }

      .rw-msg-bot {
        align-self: flex-start;
        background: #f3f4f6;
        color: #1f2937;
        border-bottom-left-radius: 4px;
      }

      .rw-msg-error {
        align-self: flex-start;
        background: #fef2f2;
        color: #dc2626;
        border-bottom-left-radius: 4px;
        font-size: 12px;
      }

      .rw-msg-welcome {
        align-self: flex-start;
        background: #f0f9ff;
        color: #1e40af;
        border-bottom-left-radius: 4px;
        font-size: 13px;
      }

      .rw-sources {
        margin-top: 8px;
        padding-top: 8px;
        border-top: 1px solid #e5e7eb;
      }

      .rw-sources-label {
        font-size: 11px;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 4px;
      }

      .rw-source-link {
        display: block;
        font-size: 12px;
        color: ${config.color};
        text-decoration: none;
        padding: 2px 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .rw-source-link:hover {
        text-decoration: underline;
      }

      .rw-source-link--yt {
        color: #ff0000;
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .rw-source-link--yt:hover {
        color: #cc0000;
      }

      .rw-yt-link {
        color: #ff0000;
        text-decoration: underline;
        display: inline-flex;
        align-items: baseline;
      }

      .rw-yt-link:hover {
        color: #cc0000;
      }

      .rw-loading {
        align-self: flex-start;
        padding: 10px 14px;
        background: #f3f4f6;
        border-radius: 10px;
        border-bottom-left-radius: 4px;
        display: flex;
        gap: 4px;
        align-items: center;
      }

      .rw-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #9ca3af;
        animation: rw-bounce 1.2s infinite;
      }

      .rw-dot:nth-child(2) { animation-delay: 0.2s; }
      .rw-dot:nth-child(3) { animation-delay: 0.4s; }

      @keyframes rw-bounce {
        0%, 60%, 100% { transform: translateY(0); }
        30% { transform: translateY(-4px); }
      }

      .rw-input-area {
        display: flex;
        padding: 12px 16px;
        gap: 8px;
        border-top: 1px solid #e5e7eb;
        flex-shrink: 0;
      }

      .rw-input {
        flex: 1;
        padding: 8px 12px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font-size: 13px;
        font-family: inherit;
        outline: none;
        transition: border-color 0.15s;
      }

      .rw-input:focus {
        border-color: ${config.color};
        box-shadow: 0 0 0 2px ${config.color}20;
      }

      .rw-input::placeholder {
        color: #9ca3af;
      }

      .rw-send {
        width: 36px;
        height: 36px;
        border-radius: 8px;
        background: ${config.color};
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: opacity 0.15s;
        flex-shrink: 0;
      }

      .rw-send:hover {
        opacity: 0.9;
      }

      .rw-send:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .rw-send:focus-visible {
        outline: 2px solid ${config.color};
        outline-offset: 2px;
      }

      .rw-send svg {
        width: 16px;
        height: 16px;
        fill: #fff;
      }

      .rw-footer {
        padding: 8px 16px;
        text-align: center;
        border-top: 1px solid #f3f4f6;
        flex-shrink: 0;
      }

      .rw-footer a {
        font-size: 11px;
        color: #9ca3af;
        text-decoration: none;
      }

      .rw-footer a:hover {
        color: #6b7280;
        text-decoration: underline;
      }

      .rw-ai-badge {
        font-size: 11px;
        color: #9ca3af;
        opacity: 0.8;
      }

      .rw-escalation {
        padding: 8px 16px;
        border-top: 1px solid #e5e7eb;
        text-align: center;
        flex-shrink: 0;
      }

      .rw-escalation-link {
        font-size: 12px;
        color: #6b7280;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }

      .rw-escalation-link:hover {
        color: ${config.color};
        text-decoration: underline;
      }

      .rw-escalation-link::before {
        content: '💬';
      }

      .rw-hidden {
        display: none !important;
      }

      .rw-msg-bot p {
        margin: 0;
      }

      .rw-msg-bot li {
        margin: 2px 0;
      }

      .rw-msg-bot strong {
        font-weight: 600;
      }

      .rw-msg-bot em {
        font-style: italic;
      }

      @media (max-width: 420px) {
        .rw-panel {
          position: fixed;
          inset: 0;
          width: 100%;
          height: 100dvh;
          max-width: 100%;
          max-height: 100dvh;
          border-radius: 0;
          animation: none;
        }

        .rw-input {
          font-size: 16px;
        }

        .rw-btn {
          right: 10px;
          left: auto;
          bottom: 14px;
          top: auto;
        }

        .rw-btn.rw-btn-hidden {
          display: none !important;
        }
      }
    `;
  }

  // SVG icons
  const ICON_SEARCH = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zM9.5 14C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>';
  const ICON_CHAT = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/><path d="M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z"/></svg>';
  const ICON_SPARKLE = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M9.937 6.223c.457-1.63.685-2.446 1.063-2.446.378 0 .606.815 1.063 2.446l.088.315c.29 1.032.435 1.548.774 1.887.34.34.855.485 1.887.774l.315.088c1.63.457 2.446.685 2.446 1.063 0 .378-.815.606-2.446 1.063l-.315.088c-1.032.29-1.548.435-1.887.774-.34.34-.485.855-.774 1.887l-.088.315c-.457 1.63-.685 2.446-1.063 2.446-.378 0-.606-.815-1.063-2.446l-.088-.315c-.29-1.032-.435-1.548-.774-1.887-.34-.34-.855-.485-1.887-.774l-.315-.088C5.242 10.956 4.427 10.728 4.427 10.35c0-.378.815-.606 2.446-1.063l.315-.088c1.032-.29 1.548-.435 1.887-.774.34-.34.485-.855.774-1.887l.088-.315zM17.5 14c.236-.84.354-1.26.548-1.26.194 0 .312.42.548 1.26l.045.162c.15.532.224.798.399.973.175.175.441.25.973.399l.162.045c.84.236 1.26.354 1.26.548 0 .194-.42.312-1.26.548l-.162.045c-.532.15-.798.224-.973.399-.175.175-.25.441-.399.973l-.045.162c-.236.84-.354 1.26-.548 1.26-.194 0-.312-.42-.548-1.26l-.045-.162c-.15-.532-.224-.798-.399-.973-.175-.175-.441-.25-.973-.399l-.162-.045c-.84-.236-1.26-.354-1.26-.548 0-.194.42-.312 1.26-.548l.162-.045c.532-.15.798-.224.973-.399.175-.175.25-.441.399-.973l.045-.162z"/></svg>';
  const ICON_HELP = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/></svg>';
  const ICON_BOOK = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z"/></svg>';
  const ICON_CLOSE = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"/></svg>';
  const ICON_SEND = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>';

  const BUTTON_ICONS = {
    search: ICON_SEARCH,
    chat: ICON_CHAT,
    sparkle: ICON_SPARKLE,
    help: ICON_HELP,
    book: ICON_BOOK,
  };

  class RagWebSearch extends HTMLElement {
    constructor() {
      super();
      this._shadow = this.attachShadow({ mode: 'closed' });
      this._config = getConfig();
      this._i18n = I18N[this._config.lang];
      this._isOpen = false;
      this._isLoading = false;
      this._sessionId = null;
      this._showBranding = true;
      this._messages = [];
      this._conversationHistory = []; // Multi-turn: stores {role, content} pairs
      this._render();
      this._attachEvents();
    }

    _render() {
      const cfg = this._config;
      const t = this._i18n;

      // Create style element
      const style = document.createElement('style');
      style.textContent = getStyles(cfg);

      // Build DOM
      const container = document.createElement('div');

      // Floating button
      const btn = document.createElement('button');
      btn.className = 'rw-btn';
      btn.setAttribute('aria-label', cfg.title);
      btn.innerHTML = BUTTON_ICONS[cfg.icon] || BUTTON_ICONS.search;
      if (cfg.shape === 'pill') {
        const label = document.createElement('span');
        label.className = 'rw-btn-label';
        label.textContent = cfg.title;
        btn.appendChild(label);
      }
      this._btn = btn;

      // Panel
      const panel = document.createElement('div');
      panel.className = 'rw-panel';
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-label', cfg.title);

      // Header
      const header = document.createElement('div');
      header.className = 'rw-header';
      header.innerHTML = `
        <span class="rw-header-title">${this._escapeHtml(cfg.title)}</span>
      `;
      const closeBtn = document.createElement('button');
      closeBtn.className = 'rw-close';
      closeBtn.setAttribute('aria-label', 'Close');
      closeBtn.innerHTML = ICON_CLOSE;
      header.appendChild(closeBtn);
      this._closeBtn = closeBtn;

      // Messages area
      const messages = document.createElement('div');
      messages.className = 'rw-messages';
      this._messagesEl = messages;

      // Welcome message
      if (cfg.welcome) {
        const welcomeMsg = document.createElement('div');
        welcomeMsg.className = 'rw-msg rw-msg-welcome';
        welcomeMsg.textContent = cfg.welcome;
        messages.appendChild(welcomeMsg);
      }

      // Input area
      const inputArea = document.createElement('div');
      inputArea.className = 'rw-input-area';

      const input = document.createElement('input');
      input.className = 'rw-input';
      input.type = 'text';
      input.placeholder = cfg.placeholder;
      input.setAttribute('aria-label', cfg.placeholder);
      this._input = input;

      const sendBtn = document.createElement('button');
      sendBtn.className = 'rw-send';
      sendBtn.setAttribute('aria-label', 'Send');
      sendBtn.innerHTML = ICON_SEND;
      this._sendBtn = sendBtn;

      inputArea.appendChild(input);
      inputArea.appendChild(sendBtn);

      // Footer (branding)
      const footer = document.createElement('div');
      footer.className = 'rw-footer';
      footer.innerHTML = `<span class="rw-ai-badge">${this._escapeHtml(t.ai_disclosure)}</span> · <a href="https://ragweb.fr" target="_blank" rel="noopener">${this._escapeHtml(t.powered_by)} RagWeb</a>`;
      this._footer = footer;

      // Assemble panel
      panel.appendChild(header);
      panel.appendChild(messages);
      panel.appendChild(inputArea);

      // Escalation bar (before footer)
      if (cfg.escalationUrl) {
        const escalationBar = document.createElement('div');
        escalationBar.className = 'rw-escalation';
        const escLink = document.createElement('a');
        escLink.href = cfg.escalationUrl;
        escLink.target = '_blank';
        escLink.rel = 'noopener noreferrer';
        escLink.className = 'rw-escalation-link';
        escLink.textContent = cfg.escalationLabel;
        escalationBar.appendChild(escLink);
        panel.appendChild(escalationBar);
      }

      panel.appendChild(footer);
      this._panel = panel;

      // Assemble container
      container.appendChild(btn);
      container.appendChild(panel);

      this._shadow.appendChild(style);
      this._shadow.appendChild(container);
    }

    _attachEvents() {
      this._btn.addEventListener('click', () => this._toggle());
      this._closeBtn.addEventListener('click', () => this._close());
      this._sendBtn.addEventListener('click', () => this._submit());
      this._input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this._submit();
        }
      });

      // Close on Escape
      this._shadow.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this._isOpen) {
          this._close();
        }
      });

      // Adapt panel height to virtual keyboard on mobile
      if (window.visualViewport) {
        const updatePanelForViewport = () => {
          if (this._isOpen && window.innerWidth <= 420) {
            const vv = window.visualViewport;
            this._panel.style.height = `${vv.height}px`;
            this._panel.style.top = `${vv.offsetTop}px`;
          }
        };
        window.visualViewport.addEventListener('resize', updatePanelForViewport);
        window.visualViewport.addEventListener('scroll', updatePanelForViewport);
      }
    }

    _toggle() {
      if (this._isOpen) {
        this._close();
      } else {
        this._open();
      }
    }

    _open() {
      this._isOpen = true;
      this._panel.classList.add('rw-open');
      if (window.innerWidth <= 420) {
        // Mobile: don't auto-focus (avoids triggering virtual keyboard)
        this._btn.classList.add('rw-btn-hidden');
        this._lockScroll();
      } else {
        this._input.focus();
      }
    }

    _close() {
      this._isOpen = false;
      this._panel.classList.remove('rw-open');
      this._btn.classList.remove('rw-btn-hidden');
      this._unlockScroll();
      this._panel.style.height = '';
      this._panel.style.top = '';
      this._btn.focus();
      // Reset conversation history on close
      this._conversationHistory = [];
    }

    async _submit() {
      const query = this._input.value.trim();
      if (!query || this._isLoading) return;

      this._input.value = '';
      this._addMessage(query, 'user');

      // Add to conversation history
      this._conversationHistory.push({ role: 'user', content: query });
      // Limit history to 6 messages (3 exchanges)
      if (this._conversationHistory.length > 6) {
        this._conversationHistory = this._conversationHistory.slice(-6);
      }

      // Choose streaming or classic mode
      if (this._config.streamUrl) {
        await this._submitStream(query);
      } else {
        await this._submitClassic(query);
      }
    }

    async _submitStream(query) {
      this._isLoading = true;
      this._sendBtn.disabled = true;

      // Create bot message element for progressive display
      const msg = document.createElement('div');
      msg.className = 'rw-msg rw-msg-bot';
      const answerEl = document.createElement('div');
      msg.appendChild(answerEl);
      this._messagesEl.appendChild(msg);
      this._scrollToBottom();

      let fullAnswer = '';
      let displayedAnswer = '';
      let sources = [];
      let typewriterQueue = '';
      let typewriterRunning = false;

      const typewriterSpeed = 15; // ms per character

      const runTypewriter = () => {
        if (typewriterRunning) return;
        typewriterRunning = true;
        const tick = () => {
          if (typewriterQueue.length === 0) {
            typewriterRunning = false;
            return;
          }
          // Display 1-3 chars at a time for natural feel
          const chunkSize = typewriterQueue.length > 20 ? 3 : 1;
          const chars = typewriterQueue.slice(0, chunkSize);
          typewriterQueue = typewriterQueue.slice(chunkSize);
          displayedAnswer += chars;
          answerEl.innerHTML = renderMarkdown(displayedAnswer);
          this._scrollToBottom();
          setTimeout(tick, typewriterSpeed);
        };
        tick();
      };

      try {
        const response = await fetch(this._config.streamUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': this._config.apiKey,
          },
          body: JSON.stringify({
            query,
            messages: this._conversationHistory.slice(0, -1),
          }),
        });

        if (!response.ok) {
          msg.remove();
          this._handleError(response.status);
          this._isLoading = false;
          this._sendBtn.disabled = false;
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop();

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));

              if (event.type === 'chunk' && event.text) {
                fullAnswer += event.text;
                typewriterQueue += event.text;
                runTypewriter();
              } else if (event.type === 'sources') {
                sources = event.sources || [];
              } else if (event.type === 'done') {
                if (typeof event.branding === 'boolean') {
                  this._showBranding = event.branding;
                  this._updateBranding();
                }
              } else if (event.type === 'error') {
                if (!fullAnswer) {
                  msg.remove();
                  this._addMessage(event.message || this._i18n.error_server, 'error');
                }
              }
            } catch (e) { /* ignore parse errors */ }
          }
        }

        // Wait for typewriter to finish before showing sources
        await new Promise((resolve) => {
          const waitForTypewriter = () => {
            if (typewriterQueue.length === 0 && !typewriterRunning) {
              resolve();
            } else {
              setTimeout(waitForTypewriter, 50);
            }
          };
          waitForTypewriter();
        });

        // Append sources if any
        if (sources.length > 0) {
          const sourcesDiv = document.createElement('div');
          sourcesDiv.className = 'rw-sources';
          const label = document.createElement('div');
          label.className = 'rw-sources-label';
          label.textContent = this._i18n.sources;
          sourcesDiv.appendChild(label);
          sources.forEach((src) => {
            const link = document.createElement('a');
            const isYt = src.type === 'youtube' || isYouTubeUrl(src.url || '');
            link.className = isYt ? 'rw-source-link rw-source-link--yt' : 'rw-source-link';
            link.href = src.url;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            if (isYt) {
              link.innerHTML = YT_ICON_SVG + '<span>' + this._escapeHtml(src.title || src.url) + '</span>';
            } else {
              link.textContent = src.title || src.url;
            }
            sourcesDiv.appendChild(link);
          });
          msg.appendChild(sourcesDiv);
        }

        // Add assistant response to conversation history
        if (fullAnswer) {
          this._conversationHistory.push({ role: 'assistant', content: fullAnswer });
        }

      } catch (err) {
        if (!fullAnswer) {
          msg.remove();
          this._addMessage(this._i18n.error_network, 'error');
        }
      }

      this._isLoading = false;
      this._sendBtn.disabled = false;
      this._scrollToBottom();
    }

    async _submitClassic(query) {
      this._showLoading();

      try {
        const response = await this._fetchSearch(query);

        this._hideLoading();

        if (!response.ok) {
          this._handleError(response.status);
          return;
        }

        const data = await response.json();

        if (data.sessionId) {
          this._sessionId = data.sessionId;
        }

        if (typeof data.branding === 'boolean') {
          this._showBranding = data.branding;
          this._updateBranding();
        }

        this._addBotMessage(data.answer, data.sources);

        // Add assistant response to conversation history
        if (data.answer) {
          this._conversationHistory.push({ role: 'assistant', content: data.answer });
        }
      } catch (err) {
        this._hideLoading();
        this._addMessage(this._i18n.error_network, 'error');
      }
    }

    async _fetchSearch(query) {
      const body = { query };
      if (this._sessionId) {
        body.sessionId = this._sessionId;
      }
      // Send conversation history for multi-turn (classic mode)
      if (this._conversationHistory.length > 1) {
        body.messages = this._conversationHistory.slice(0, -1); // Exclude current query
      }

      return fetch(this._config.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this._config.apiKey,
        },
        body: JSON.stringify(body),
      });
    }

    _handleError(status) {
      const t = this._i18n;
      let msg;
      switch (status) {
        case 429:
          msg = t.error_rate_limit;
          break;
        case 400:
          msg = t.error_invalid;
          break;
        case 402:
          msg = t.error_expired;
          break;
        case 500:
        case 503:
          msg = t.error_server;
          break;
        default:
          msg = t.error_network;
      }
      this._addMessage(msg, 'error');
    }

    _addMessage(text, type) {
      const msg = document.createElement('div');
      msg.className = `rw-msg rw-msg-${type}`;
      msg.textContent = text;
      this._messagesEl.appendChild(msg);
      this._scrollToBottom();
    }

    _addBotMessage(answer, sources) {
      const msg = document.createElement('div');
      msg.className = 'rw-msg rw-msg-bot';

      // Answer text
      const answerText = document.createElement('div');
      answerText.innerHTML = renderMarkdown(answer);
      msg.appendChild(answerText);

      // Sources
      if (sources && sources.length > 0) {
        const sourcesDiv = document.createElement('div');
        sourcesDiv.className = 'rw-sources';

        const label = document.createElement('div');
        label.className = 'rw-sources-label';
        label.textContent = this._i18n.sources;
        sourcesDiv.appendChild(label);

        sources.forEach((src) => {
          const link = document.createElement('a');
          const isYt = src.type === 'youtube' || isYouTubeUrl(src.url || '');
          link.className = isYt ? 'rw-source-link rw-source-link--yt' : 'rw-source-link';
          link.href = src.url;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          if (isYt) {
            link.innerHTML = YT_ICON_SVG + '<span>' + this._escapeHtml(src.title || src.url) + '</span>';
          } else {
            link.textContent = src.title || src.url;
          }
          sourcesDiv.appendChild(link);
        });

        msg.appendChild(sourcesDiv);
      }

      this._messagesEl.appendChild(msg);
      this._scrollToBottom();
    }

    _showLoading() {
      this._isLoading = true;
      this._sendBtn.disabled = true;
      const loader = document.createElement('div');
      loader.className = 'rw-loading';
      loader.id = 'rw-loader';
      loader.innerHTML = '<div class="rw-dot"></div><div class="rw-dot"></div><div class="rw-dot"></div>';
      this._messagesEl.appendChild(loader);
      this._scrollToBottom();
    }

    _hideLoading() {
      this._isLoading = false;
      this._sendBtn.disabled = false;
      const loader = this._shadow.getElementById('rw-loader');
      if (loader) loader.remove();
    }

    _updateBranding() {
      if (this._showBranding) {
        this._footer.classList.remove('rw-hidden');
      } else {
        this._footer.classList.add('rw-hidden');
      }
    }

    _scrollToBottom() {
      this._messagesEl.scrollTop = this._messagesEl.scrollHeight;
    }

    _lockScroll() {
      this._scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this._scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    }

    _unlockScroll() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      if (this._scrollY !== undefined) {
        window.scrollTo(0, this._scrollY);
      }
    }

    _escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  // Register custom element
  if (!customElements.get('ragweb-search')) {
    customElements.define('ragweb-search', RagWebSearch);
  }

  // Path filtering: check if widget should show on current page
  if (!shouldShowOnPage(getConfig())) return;

  // Remove any existing widget (for reloads with new config)
  var existingWidget = document.querySelector('ragweb-search');
  if (existingWidget) existingWidget.remove();

  // Self-initialize: create and append the widget element
  const widget = document.createElement('ragweb-search');
  document.body.appendChild(widget);
})();
